/* Dataset card on the project detail view.
 *
 * Sits below the Lines card and mirrors its chrome exactly (inline styles, as
 * the other summary bars do). The section is feature-flagged on the backend:
 * a 404 from the list route means the routes are not deployed, and the card
 * renders nothing at all rather than an error.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { API, NATURE_IDS, natureMeta, spanLabel, statusColor } from './datasetCommon';

const ACTIVE = new Set(['staged', 'reconciling', 'loading', 'compressing', 'aggregating']);

const DatasetSummary = ({ project, onOpen }) => {
  const [datasets, setDatasets] = useState(null);   // null = not loaded yet
  const [enabled, setEnabled] = useState(true);
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/projects/${project.id}/datasets`);
      if (res.status === 404) { setEnabled(false); return; }
      if (!res.ok) { setDatasets([]); return; }
      const rows = await res.json();
      setDatasets(Array.isArray(rows) ? rows : []);
    } catch {
      setDatasets([]);
    }
  }, [project.id]);

  useEffect(() => { load(); }, [load]);

  // Keep the card honest while an import is running, and stop as soon as it
  // settles — this card sits on a page the user may leave open all day.
  useEffect(() => {
    if (!datasets || !datasets.some((d) => ACTIVE.has(d.status))) return undefined;
    timer.current = setInterval(load, 5000);
    return () => clearInterval(timer.current);
  }, [datasets, load]);

  if (!enabled || datasets === null) return null;

  const latest = datasets.length
    ? [...datasets].sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')))[0]
    : null;

  const dot = latest ? statusColor(latest.status) : '#94a3b8';
  const natures = (latest && latest.natures) || {};
  const chips = NATURE_IDS.filter((n) => natures[n]).map((n) => [n, natures[n]]);

  const facts = latest
    ? [
        latest.series_count ? `${latest.series_count} series` : null,
        spanLabel(latest),
        latest.status,
      ].filter(Boolean).join(' · ')
    : null;

  return (
    <div style={{
      background: '#1e293b',
      border: '1px solid #334155',
      borderLeft: `4px solid ${dot}`,
      borderRadius: 6,
      marginBottom: 12,
      padding: '10px 16px',
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      flexWrap: 'wrap',
    }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: '#e2e8f0', fontFamily: "'Space Grotesk', sans-serif" }}>
        <span style={{ color: dot, marginRight: 8 }}>{latest ? '●' : '○'}</span>Dataset
      </div>

      {!latest ? (
        <>
          <div style={{ fontSize: 11, color: '#94a3b8', fontFamily: "'JetBrains Mono', monospace" }}>
            No dataset loaded
          </div>
          <div style={{ flex: 1 }} />
          <button className="btn-accent" onClick={onOpen}>Import →</button>
        </>
      ) : (
        <>
          <div style={{ fontSize: 11, color: '#94a3b8', fontFamily: "'JetBrains Mono', monospace" }}>
            {datasets.length} dataset{datasets.length === 1 ? '' : 's'}
          </div>
          <div style={{ fontSize: 12, color: '#e2e8f0', fontFamily: "'Space Grotesk', sans-serif" }}>
            {latest.name || 'unnamed'}
          </div>
          <div style={{ fontSize: 11, color: '#94a3b8', fontFamily: "'JetBrains Mono', monospace" }}>
            {facts}
          </div>
          {latest.status === 'failed' && latest.error && (
            <div style={{ fontSize: 11, color: '#f87171', fontFamily: "'JetBrains Mono', monospace" }}
                 title={latest.error}>
              {String(latest.error).slice(0, 60)}
            </div>
          )}
          {chips.length > 0 && (
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {chips.map(([n, count]) => {
                const m = natureMeta(n);
                return (
                  <span key={n} style={{
                    background: m.color, color: '#0f172a', padding: '2px 8px', borderRadius: 3,
                    fontSize: 10, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace",
                  }}>
                    {m.label} · {count}
                  </span>
                );
              })}
            </div>
          )}
          <div style={{ flex: 1 }} />
          <button className="btn-accent" onClick={onOpen}>Open →</button>
        </>
      )}
    </div>
  );
};

export default DatasetSummary;
